import { Routes } from '@angular/router';
import { avecReprise } from '@app/chargement-ecran';
import { vueCollabGuard } from '@app/acces-complet';
import { horsArchiveGuard } from '@app/archive-situation';
import { TypeListe } from './liste/liste-declarations.model';

/*
 * Écrans des déclarations et des états financiers du collaborateur, repris du bureau (onglets → écrans routés),
 * relatifs au dossier. Importé seulement derrière LOGAIA_COLLAB_BUILD (app.routes.ts) : absent du build « client
 * seul ». Collaborateur, ou client avec accès complet (vueCollabGuard).
 */

// Chaque fonction teste LOGAIA_COLLAB_BUILD elle-même, en ternaire (replié dès la lecture du fichier) : sans cela,
// le build « client seul » émettrait les écrans en fichiers orphelins (imports dynamiques vus avant l'élimination
// du code mort).

const liste: Routes[number]['loadComponent'] = LOGAIA_COLLAB_BUILD
  ? () => avecReprise(() => import('./liste/liste-declarations.component')).then((c) => c.ListeDeclarationsComponent)
  : undefined;

/** Liste du bureau ouverte par un menu : type et titre (libellé du menu). */
function routeListe(path: string, type: TypeListe, titre: string): Routes[number] {
  return { path, canMatch: [vueCollabGuard], data: { type, titre }, loadComponent: liste };
}

/**
 * Liste TVA du collaborateur (menu Déclarations › TVA) : même adresse que les déclarations de TVA du client, placée
 * avant la route du client (un client sans accès complet garde son écran).
 */
export function routeTvaCollab(): Routes {
  return LOGAIA_COLLAB_BUILD ? [routeListe('declarations', 'tva', 'TVA')] : [];
}

/** Autres écrans (refusés au client sans accès complet). */
export function routesDeclarationsCollab(): Routes {
  return LOGAIA_COLLAB_BUILD ? [
    routeListe('impots-societes', 'is', 'Impôts sur sociétés'),
    routeListe('cvae', 'cvae', 'CVAE'),
    routeListe('versement-pfu', 'pfu', 'Versement PFU'),
    {
      path: 'calcul-tvs/:declarationId',
      canMatch: [vueCollabGuard],
      loadComponent: () => avecReprise(() => import('./tvs/calcul-tvs.component')).then((c) => c.CalculTvsComponent),
    },
    {
      // Saisie générique d'une déclaration ; ouverture refusée en archive en production (écran).
      path: 'etats-financiers/declaration/:declarationId',
      canMatch: [vueCollabGuard],
      loadComponent: () =>
        avecReprise(() => import('./saisie/saisie-declaration.component')).then((c) => c.SaisieDeclarationComponent),
    },
    {
      // Entrée d'un menu des états (États financiers, Fiscal, Plaquette, Paramétrage des états).
      path: 'etats-financiers/document/:code',
      canMatch: [vueCollabGuard],
      loadComponent: () =>
        avecReprise(() => import('./document/ouverture-document.component')).then((c) => c.OuvertureDocumentComponent),
    },
    routeListe('etats-financiers/historique-liasses', 'historique-liasses', 'Historique liasses'),
    {
      // Menu « Paramétrage des états » : grisé en archive au bureau.
      path: 'etats-financiers/historique-rubriques',
      canMatch: [vueCollabGuard],
      canActivate: [horsArchiveGuard],
      loadComponent: () =>
        avecReprise(() => import('./historique-rubriques/historique-rubriques.component')).then((c) => c.HistoriqueRubriquesComponent),
    },
  ] : [];
}
